import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveLocale } from '../utils/vukzLocale';
import SEOHead from '../components/seo/SEOHead';
import ProfilesCarousel from '../marketing/ProfilesCarousel';
import AssistProfilesCarousel, { ASSIST_PROFILES_EN } from '../marketing/AssistProfilesCarousel';
import { OrganizationSchema } from '../components/seo/StructuredData';
import {
  ArrowRight,
  BarChart3,
  Activity,
  Users,
  Zap,
  ChevronRight,
  Building2,
  Menu,
  X,
  ClipboardList,
  HeartPulse,
  Gauge,
  LineChart,
  Apple,
  Dumbbell,
  MessageSquare,
  Sparkles,
} from 'lucide-react';

/* ──────────────────────────────────────────────
   VUKZ Brand Tokens — scoped to this page only
   ────────────────────────────────────────────── */
const V = {
  bg: '#ffffff',
  text: '#111111',
  textSoft: '#555555',
  textMute: '#8a8a8a',
  accent: '#111111',
  accentHover: '#333333',
  border: '#e6e6e6',
  cardBg: '#fafafa',
  tagBg: '#f3f3f3',
};

/* ─────── Header navigation (anchor links) ─────── */
const NAV_LINKS = [
  { href: '#plataforma', label: 'Platform' },
  { href: '#assist', label: 'VUKZ Assist' },
  { href: '#perfis', label: 'Roles' },
  { href: '#funcionalidades', label: 'Features' },
  { href: '#planos', label: 'Plans' },
];

/* ─────── Integrated platform flow ─────── */
const FLOW = ['Planning', 'Training', 'Execution', 'Load', 'Wellness', 'Nutrition', 'Performance', 'Management'];

/* ─────── The five real roles ─────── */
const PROFILES = [
  {
    icon: Activity,
    label: 'Athlete',
    desc: 'The daily routine in one place.',
    items: ["Today's training and guided execution", 'Wellness check-in', 'Personal performance tracking', 'VUKZ Assist support'],
  },
  {
    icon: Users,
    label: 'Coach',
    desc: 'Plan, follow and adjust with data.',
    items: ['Athlete management and session planning', 'Execution follow-up', 'Squad wellness overview', 'Coach Agent support'],
  },
  {
    icon: Apple,
    label: 'Nutritionist',
    desc: 'Sports nutrition connected to the team.',
    items: ['Nutrition dashboard and assessments', 'Meal plans', 'Hydration and supplement tracking', 'Nutrition alerts'],
  },
  {
    icon: Dumbbell,
    label: 'Physical Trainer',
    desc: 'Load under control, day after day.',
    items: ['Physical session builder', 'Load monitoring and ACWR', 'Risk alerts', 'Prep Agent support'],
  },
  {
    icon: BarChart3,
    label: 'Manager',
    desc: 'The whole club in one view.',
    items: ['Aggregated performance indicators', 'Roster, staff and categories', 'Methodology and strategic planning', 'Institution management'],
  },
];

/* ─────── Confirmed cross-role features ─────── */
const FEATURES = [
  { icon: ClipboardList, label: 'Training & execution', desc: 'Plan sessions and follow execution in real workflows.' },
  { icon: HeartPulse, label: 'Wellness & readiness', desc: 'Daily check-ins for athletes and squad-level views for staff.' },
  { icon: Gauge, label: 'Load monitoring', desc: 'Training load and ACWR with alerts for the technical staff.' },
  { icon: LineChart, label: 'Performance & progression', desc: 'Track results and evolution over time.' },
  { icon: Apple, label: 'Sports nutrition', desc: 'Assessments, meal plans, hydration and supplements.' },
  { icon: Users, label: 'Roster & team management', desc: 'Athletes, staff, categories and scopes in one place.' },
  { icon: MessageSquare, label: 'Communication', desc: 'Direct messages between the people who run the team.' },
  { icon: Sparkles, label: 'AI for each role', desc: 'VUKZ Assist adapts to the profile of whoever is using it.' },
];

const Container = ({ children, className = '' }) => (
  <div className={`max-w-6xl mx-auto px-6 ${className}`}>{children}</div>
);

/* Static screenshot slot — renders the real demo screenshot when the asset
   exists in public/screenshots/. Falls back to a neutral brand block (no
   simulated metrics or fake data) while the capture is not available. */
const ScreenshotSlot = ({ src, alt, label, className = '' }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={`w-full aspect-[16/10] rounded-2xl border flex flex-col items-center justify-center gap-3 ${className}`}
        style={{ borderColor: V.border, backgroundColor: V.cardBg }}
      >
        <img src="/vukz-logo.jpeg" alt="" className="h-10 w-auto" />
        <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: V.textMute }}>
          {label}
        </p>
        <p className="text-[11px]" style={{ color: V.textMute }}>
          Demo screenshot coming soon
        </p>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-full rounded-2xl border object-cover ${className}`}
      style={{ borderColor: V.border }}
    />
  );
};

/* ═══════════════════════════════════════════════
   VukzHomePage — public marketing shell
   ═══════════════════════════════════════════════ */
const VukzHomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  // Persiste o idioma do site público (vukz_locale) — herdado por /request-access
  useEffect(() => { saveLocale('en'); }, []);

  return (
    <div style={{ backgroundColor: V.bg, color: V.text }} className="min-h-screen font-sans antialiased">
      <SEOHead
        title="VUKZ — Sports Intelligence OS"
        description="VUKZ — AI-powered sports intelligence and governance OS for clubs, federations, coaches and performance teams."
        path="/"
        lang="en"
        alternatePath="/pt"
      />
      <OrganizationSchema />
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 px-4 py-2 rounded-lg font-bold"
        style={{ backgroundColor: V.accent, color: '#ffffff' }}
      >
        Skip to main content
      </a>

      {/* ── NAVBAR ── */}
      <nav
        className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b"
        style={{ borderColor: V.border }}
        role="navigation"
        aria-label="Main navigation"
      >
        <Container className="h-16 flex items-center justify-between gap-4">
          {/* Group 1 — Brand */}
          <Link to={isAuthenticated ? '/central' : '/'} className="flex items-center gap-3 flex-shrink-0" aria-label="VUKZ - Home">
            <img src="/vukz-logo.jpeg" alt="VUKZ Performance Intelligence Platform" className="h-10 w-auto" />
          </Link>

          {/* Group 2 — Navigation (desktop) */}
          <nav className="hidden lg:flex items-center gap-6" aria-label="Page sections">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium hover:opacity-70 transition-opacity whitespace-nowrap"
                style={{ color: V.textSoft }}
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Group 3 — Actions */}
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="hidden lg:block h-5 w-px" style={{ backgroundColor: V.border }} />

            {/* Language switcher */}
            <div className="flex items-center rounded-lg border overflow-hidden" style={{ borderColor: V.border }} aria-label="Language">
              <span
                className="px-3 py-1.5 text-xs font-bold"
                style={{ backgroundColor: V.accent, color: '#fff' }}
                aria-current="true"
                lang="en"
              >
                EN
              </span>
              <Link
                to="/pt"
                className="px-3 py-1.5 text-xs font-semibold hover:bg-gray-100 transition-colors"
                style={{ color: V.textSoft }}
                lang="pt-BR"
              >
                PT
              </Link>
            </div>

            {isAuthenticated ? (
              <button
                onClick={() => navigate('/central')}
                className="text-sm font-bold px-4 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: V.accent }}
              >
                Open app
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-block text-sm font-semibold px-3 py-2 rounded-lg transition-opacity hover:opacity-70"
                  style={{ color: V.text }}
                >
                  Login
                </Link>
                <Link
                  to="/request-access"
                  className="hidden sm:inline-block text-sm font-bold px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: V.accent }}
                >
                  Request access
                </Link>
              </>
            )}

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold"
              style={{ borderColor: V.border, color: V.text }}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
              <span>Menu</span>
            </button>
          </div>
        </Container>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div id="mobile-menu" className="lg:hidden border-t bg-white" style={{ borderColor: V.border }}>
            <Container className="py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={closeMobile}
                  className="py-2.5 px-2 rounded-lg text-sm font-medium hover:bg-gray-50"
                  style={{ color: V.textSoft }}
                >
                  {l.label}
                </a>
              ))}
              {!isAuthenticated && (
                <div className="flex items-center gap-3 pt-3 mt-2 border-t" style={{ borderColor: V.border }}>
                  <Link
                    to="/login"
                    onClick={closeMobile}
                    className="flex-1 text-center text-sm font-semibold px-3 py-2.5 rounded-lg border"
                    style={{ borderColor: V.border, color: V.text }}
                  >
                    Login
                  </Link>
                  <Link
                    to="/request-access"
                    onClick={closeMobile}
                    className="flex-1 text-center text-sm font-bold px-3 py-2.5 rounded-lg text-white"
                    style={{ backgroundColor: V.accent }}
                  >
                    Request access
                  </Link>
                </div>
              )}
            </Container>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <main id="main-content" className="pt-28">
        <section className="pb-14">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              {/* Left */}
              <div className="lg:col-span-6">
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-7"
                  style={{ backgroundColor: V.tagBg, color: V.textSoft }}
                >
                  <Zap size={14} />
                  AI-first sports management
                </div>

                <h1 className="text-5xl sm:text-6xl font-black tracking-tight leading-[1.02]">
                  VUKZ
                  <span className="block text-2xl sm:text-3xl font-bold mt-3" style={{ color: V.textSoft }}>
                    AI-first sports management, built for swimming teams.
                  </span>
                </h1>

                <p className="text-base sm:text-lg mt-6 max-w-xl leading-relaxed" style={{ color: V.textSoft }}>
                  VUKZ connects training, load, wellness, nutrition, performance and club management
                  in one system — designed for athletes, coaches, nutritionists, physical trainers and managers.
                </p>

                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 mt-9">
                  {isAuthenticated ? (
                    <button
                      onClick={() => navigate('/central')}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90"
                      style={{ backgroundColor: V.accent }}
                    >
                      Open app <ArrowRight size={18} aria-hidden="true" />
                    </button>
                  ) : (
                    <Link
                      to="/request-access"
                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90"
                      style={{ backgroundColor: V.accent }}
                    >
                      <Building2 size={18} aria-hidden="true" /> Request access <ArrowRight size={18} aria-hidden="true" />
                    </Link>
                  )}
                  <a
                    href="#plataforma"
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3 rounded-xl font-bold text-base border transition-opacity hover:opacity-80"
                    style={{ borderColor: V.border, color: V.textSoft }}
                  >
                    See the platform <ChevronRight size={18} aria-hidden="true" />
                  </a>
                </div>

                <div className="mt-10 flex items-center gap-3 text-xs" style={{ color: V.textMute }}>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#22c55e' }} />
                    Hosted on Azure
                  </span>
                  <span>•</span>
                  <span>RBAC &amp; enterprise modules</span>
                </div>
              </div>

              {/* Right: current product screenshot (synthetic showcase) */}
              <div className="lg:col-span-6">
                <ScreenshotSlot
                  src="/marketing/screens/manager-today.png"
                  alt="Manager Today screen on VUKZ with club-wide overview (synthetic demo data)"
                  label="Manager view — Today"
                />
                <p className="text-xs mt-3 text-center" style={{ color: V.textMute }}>
                  Real product screen — demo environment data.
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* ── INTEGRATED PLATFORM ── */}
        <section id="plataforma" className="py-20 border-t" style={{ borderColor: V.border }}>
          <Container>
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">One integrated platform</h2>
              <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                From planning to management — every step of the team&apos;s routine connected in a single system.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2" aria-label="Platform flow">
              {FLOW.map((step, i) => (
                <React.Fragment key={step}>
                  <span
                    className="px-4 py-2 rounded-xl border text-sm font-semibold bg-white"
                    style={{ borderColor: V.border, color: V.text }}
                  >
                    {step}
                  </span>
                  {i < FLOW.length - 1 && (
                    <ChevronRight size={16} aria-hidden="true" style={{ color: V.textMute }} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="text-xs text-center mt-8" style={{ color: V.textMute }}>
              Initial focus on swimming.
            </p>
          </Container>
        </section>

        {/* ── VUKZ ASSIST — CHAT CAROUSEL ── */}
        <AssistProfilesCarousel
          title="One assistant for every role on the team"
          subtitle="Each profile gets contextual support to organize the routine, interpret data and make decisions with more clarity."
          ariaLabel="The five VUKZ Assist chats by profile"
          inputPlaceholder="Type your message"
          prevLabel="Previous"
          nextLabel="Next"
          profiles={ASSIST_PROFILES_EN}
        />

        {/* ── FIVE ROLES ── */}
        <section id="perfis" className="py-20">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Five roles, one system</h2>
              <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                Each role has its own workspace, permissions and workflows.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PROFILES.map((p) => (
                <div
                  key={p.label}
                  className="flex flex-col p-6 rounded-2xl border bg-white transition-all hover:shadow-md"
                  style={{ borderColor: V.border }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: V.tagBg }}
                    aria-hidden="true"
                  >
                    <p.icon size={22} style={{ color: V.text }} strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-bold" style={{ color: V.text }}>{p.label}</h3>
                  <p className="text-sm mt-1 mb-4" style={{ color: V.textSoft }}>{p.desc}</p>
                  <ul className="space-y-2 text-sm mt-auto" style={{ color: V.textSoft }}>
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5">
                        <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: V.textMute }} aria-hidden="true" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── FIVE VIEWS CAROUSEL ── */}
        <ProfilesCarousel
          title="One platform. Five views. One team."
          subtitle="Every profile opens the day with exactly what matters: the athlete sees their plan, the staff sees their groups, the manager sees the whole club."
          ariaLabel="The five Today screens by profile"
          slides={[
            { img: 'athlete-today', title: 'Athlete — Today', alt: 'Athlete Today screen with readiness, training and nutrition', description: 'Readiness, today\u2019s training, meals and hydration in a single view.' },
            { img: 'coach-today', title: 'Coach — Today', alt: 'Coach Today screen with training groups', description: 'Training groups, volumes and session status for the whole squad.' },
            { img: 'prep-today', title: 'Physical trainer — Today', alt: 'Physical trainer Today screen with load and ACWR', description: 'Strength groups, load control and technical context in read-only.' },
            { img: 'nutritionist-today', title: 'Nutritionist — Today', alt: 'Nutritionist Today screen with plan coverage by category', description: 'Plan coverage by category and who needs attention today.' },
            { img: 'manager-today', title: 'Manager — Today', alt: 'Manager Today screen with club-wide overview', description: 'Club-wide view: athletes, staff, categories and daily activity.' },
          ]}
        />

        {/* ── FEATURES ── */}
        <section id="funcionalidades" className="py-20" style={{ backgroundColor: V.cardBg }}>
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">What you can do today</h2>
              <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                Capabilities already available in the platform.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {FEATURES.map((f) => (
                <div
                  key={f.label}
                  className="p-6 rounded-2xl border bg-white transition-all hover:shadow-md"
                  style={{ borderColor: V.border }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: V.tagBg }}
                    aria-hidden="true"
                  >
                    <f.icon size={22} style={{ color: V.text }} strokeWidth={2} />
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: V.text }}>{f.label}</h3>
                  <p className="text-sm leading-relaxed mt-2" style={{ color: V.textSoft }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── PLANS ── */}
        <section id="planos" className="py-20 border-t" style={{ borderColor: V.border, backgroundColor: V.cardBg }}>
          <Container className="max-w-3xl">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Plans for every team</h2>
              <p className="text-base mt-4 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                Options for different team sizes, progressive access to features and the possibility
                of a custom plan for larger organizations.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90"
                style={{ backgroundColor: V.accent }}
              >
                See plans <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </Container>
        </section>

        {/* ── INFRA ── */}
        <section className="py-14 border-t" style={{ borderColor: V.border }}>
          <Container className="max-w-4xl">
            <div className="flex flex-col items-center text-center gap-6">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: V.textMute }}>
                Program &amp; infrastructure
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="/microsoft-logo.png"
                  alt="Microsoft for Startups"
                  className="h-10 w-auto"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="text-left">
                  <p className="font-bold text-sm" style={{ color: V.text }}>Microsoft for Startups</p>
                  <p className="text-xs" style={{ color: V.textMute }}>Founders Hub member · Built on Azure</p>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ── FINAL CTA ── */}
        <section>
          <div className="py-16" style={{ backgroundColor: V.text }}>
            <Container className="max-w-2xl">
              <div className="text-center">
                <Building2 size={32} className="mx-auto mb-6" style={{ color: '#ffffff' }} strokeWidth={1.5} />
                <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  Activate your club on VUKZ
                </h2>
                <p className="text-base mt-4 mb-8" style={{ color: 'rgba(255,255,255,0.65)' }}>
                  Set up your environment, invite your staff and start operating with sports intelligence.
                </p>
                <Link
                  to={isAuthenticated ? '/central' : '/request-access'}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#ffffff', color: V.text }}
                >
                  {isAuthenticated ? 'Go to app' : 'Request access'} <ArrowRight size={18} />
                </Link>
              </div>
            </Container>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="py-8 border-t" style={{ borderColor: V.border, backgroundColor: V.bg }} role="contentinfo">
          <Container>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" style={{ color: V.textMute }}>
              <div className="text-center sm:text-left">
                <span className="font-bold" style={{ color: V.text }}>VUKZ</span>
                <p className="text-xs mt-1">AI-first sports management for swimming teams.</p>
                <p className="text-xs mt-2">© {new Date().getFullYear()} VUKZ. All rights reserved.</p>
              </div>
              <nav aria-label="Footer links" className="flex flex-wrap justify-center gap-x-6 gap-y-2">
                <Link to="/pricing" className="hover:underline">Plans</Link>
                <Link to="/login" className="hover:underline">Login</Link>
                <Link to="/en/privacy" className="hover:underline">Privacy</Link>
                <Link to="/en/terms" className="hover:underline">Terms</Link>
                <Link to="/en/sla" className="hover:underline">SLA</Link>
                <Link to="/contato" className="hover:underline">Contact</Link>
                <Link to="/pt" className="hover:underline" lang="pt-BR">Português</Link>
              </nav>
            </div>
          </Container>
        </footer>
      </main>
    </div>
  );
};

export default VukzHomePage;
